// DXF/DWG utility plan extract (e.g. Leitungskataster Luzern / Zürich) -> FeatureCollection for featuresToNet
// layers get a suggested role (pipe + system, manhole/node, label, ignore) that the user confirms
const RULES = [
  [/text|beschr|label|anno|bemass|dim|schrift|txt/i, 'text'],
  [/schacht|knoten|regard|manhole|node|kontroll|hydrant|schieber|armatur|valve|\bks\b/i, 'node'],
  [/gas|elektr|strom|\belt?\b|fernw|\bfw\b|tele|komm|swisscom|kabel|cable|beleucht|geb[aä]ude|parzell|grenze|strasse|bau/i, 'ignore'],
  [/regen|meteor|pluv|\brw\b|sauber|clair/i, 'pipe', 'RW'],
  [/misch|\bmw\b|mixte|unitaire/i, 'pipe', 'MW'],
  [/schmutz|abwasser|kanal|\bsw\b|\beu\b|us[eé]e|sewer|\bkan/i, 'pipe', 'SW'],
  [/wasser|trink|\bwv\b|water|\baep\b|\beau\b|versorg/i, 'pipe', 'W'],
];
export const DN_RE = /(?:\bDN|\bNW|Ø|⌀|\bD\s*=)\s*(\d{2,4})\b/i;

export function dxfLayers(ents, kind) {
  const m = {};
  ents.forEach((e) => { const r = (m[e.layer] ||= { layer: e.layer, lines: 0, points: 0, texts: 0 }); r[e.type === 'line' ? 'lines' : e.type === 'text' ? 'texts' : 'points']++; });
  return Object.values(m).map((r) => {
    const hit = RULES.find(([re]) => re.test(r.layer));
    let role = hit?.[1] || (r.texts > r.lines + r.points ? 'text' : 'ignore'), sys = hit?.[2] || (kind === 'sewer' ? 'SW' : 'W');
    const net = RULES.slice(3).find(([re]) => re.test(r.layer))?.[2];
    if (role !== 'ignore' && net && (kind === 'sewer') === (net === 'W')) role = 'ignore';
    else if (net && role === 'pipe') sys = net;
    if (role === 'node' && !r.points && !r.lines) role = 'ignore';
    return { ...r, role, sys };
  }).sort((a, b) => (a.role === 'ignore') - (b.role === 'ignore') || b.lines + b.points - a.lines - a.points);
}

const SYS = { RW: 'Regenwasser', MW: 'Mischwasser', SW: 'Schmutzwasser', W: 'Trinkwasser' };
const segD = (p, a, b) => { const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy, t = L2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0; return [Math.hypot(a[0] + t * dx - p[0], a[1] + t * dy - p[1]), t]; };
const lineD = (p, pts) => Math.min(...pts.slice(1).map((b, i) => segD(p, pts[i], b)[0]));
const ext = (pts) => { const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1]); return Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)); };
const mean = (pts) => [pts.reduce((s, q) => s + q[0], 0) / pts.length, pts.reduce((s, q) => s + q[1], 0) / pts.length];

// raw drawing coordinates stay untouched (featuresToNet detects LV95 / LV03 / UTM); pipes are split at manholes lying on them
export function dxfToFc(ents, layers) {
  const R = Object.fromEntries(layers.map((l) => [l.layer, l])), role = (e) => R[e.layer]?.role;
  const nodes = [];
  ents.filter((e) => role(e) === 'node').forEach((e) => {
    if (e.type === 'point' || e.type === 'circle') nodes.push({ c: e.p, layer: e.layer });
    else if (e.type === 'line' && ext(e.pts) < 3) nodes.push({ c: mean(e.pts), layer: e.layer });
  });
  const pipes = [];
  ents.filter((e) => e.type === 'line' && role(e) === 'pipe').forEach((e) => {
    // cut points: manhole symbols within 0.3 m of the polyline
    const cuts = [];
    e.pts.slice(1).forEach((b, i) => nodes.forEach((n) => { const [d, t] = segD(n.c, e.pts[i], b); if (d <= 0.3 && (i > 0 || t > 0.001) && (i < e.pts.length - 2 || t < 0.999)) cuts.push([i + t, n.c]); }));
    cuts.sort((a, b) => a[0] - b[0]);
    let cur = [e.pts[0]], k = 0;
    e.pts.slice(1).forEach((b, i) => {
      while (k < cuts.length && cuts[k][0] < i + 1) { cur.push(cuts[k][1]); if (cur.length > 1) pipes.push({ pts: cur, layer: e.layer }); cur = [cuts[k][1]]; k++; }
      cur.push(b);
    });
    if (cur.length > 1) pipes.push({ pts: cur, layer: e.layer });
  });
  // labels: "DN 150" next to exactly one pipe (<= 3 m, next pipe clearly farther) -> diameter from label; other short texts name a manhole
  const log = { dnTxt: 0, dnAmb: 0, dnConf: 0, names: 0 };
  ents.filter((e) => e.type === 'text' && role(e) !== 'ignore' && e.text).forEach((e) => {
    const m = e.text.match(DN_RE);
    if (m) {
      const ds = pipes.map((p, i) => [lineD(e.p, p.pts), i]).sort((a, b) => a[0] - b[0]);
      if (!ds.length || ds[0][0] > 3 || (ds[1] && ds[1][0] < ds[0][0] * 1.5 && ds[1][0] < 3)) { log.dnAmb++; return; }
      const p = pipes[ds[0][1]];
      if (p.dn && p.dn !== +m[1]) { p.conf = true; log.dnConf++; return; }
      p.dn = +m[1];
      return;
    }
    const t = e.text.trim();
    if (t.length > 12) return;
    const n = nodes.map((q) => [Math.hypot(q.c[0] - e.p[0], q.c[1] - e.p[1]), q]).sort((a, b) => a[0] - b[0])[0];
    if (n && n[0] <= 2 && !n[1].name) { n[1].name = t; log.names++; }
  });
  pipes.forEach((p) => { if (p.conf) delete p.dn; else if (p.dn) log.dnTxt++; });
  const features = [
    ...pipes.map((p) => ({ type: 'Feature', geometry: { type: 'LineString', coordinates: p.pts }, properties: { layer: p.layer, system: SYS[R[p.layer].sys], ...(p.dn ? { dn: p.dn, dn_src: 'beschriftung' } : {}) } })),
    ...nodes.map((n) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: n.c }, properties: { layer: n.layer, type: n.layer, ...(n.name ? { name: n.name } : {}) } })),
  ];
  return { fc: { type: 'FeatureCollection', features }, log };
}
